import { useEffect, useState } from "react";
import { useNavigate, NavLink } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import Nav from "./Nav";
import { fetchSales, updateSalesStatus, deleteSales } from "./api";

const Sales = () => {
  const nav = useNavigate();
  const queryClient = useQueryClient();
  const [searchQuery, setSearchQuery] = useState("");

  // Fetch sales data using object syntax
  const { data: sales = [] } = useQuery({
    queryKey: ["sales"],
    queryFn: fetchSales,
  });

  // Update status mutation
  const updateStatusMutation = useMutation({
    mutationFn: updateSalesStatus,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["sales"] }),
  });

  // Delete row mutation
  const deleteMutation = useMutation({
    mutationFn: deleteSales,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["sales"] }),
  });

  const [salesData, setSalesData] = useState([]); // local copy for editing
  useEffect(() => {
    setSalesData(sales); // copy fetched data to local state
  }, [sales]);
  // Update a specific field in a row
  const updateField = (id, index, value) => {
    setSalesData((prev) =>
      prev.map((row) => (row[0] === id ? { ...row, [index]: value } : row))
    );
  };
  // Filtered data based on search query
  const filteredSales = salesData.filter((k) => {
    const q = searchQuery.toLowerCase();

    return (
      k[1] === searchQuery ||
      k[0] === searchQuery ||
      (k[2] || "").toLowerCase().includes(q) ||
      (k[3] || "").toLowerCase().includes(q) ||
      String(k[4] || "")
        .toLowerCase()
        .includes(q)
    );
  });
  const handleStatusToggle = (row) => {
    // Toggle local edit state
    setEditableRows((prev) => ({
      ...prev,
      [row[0]]: !prev[row[0]],
    }));

    // Optionally update backend status
    const updatedRow = [...Object.values(row)];
    updateStatusMutation.mutate(updatedRow);
  };

  const handleDelete = (id) => {
    deleteMutation.mutate(id);
  };

  const viewCustomer = (id) => nav("/viewcustomer", { state: id });

  const viewSales = (row) => {
    const selectedData = [
      row[0],
      row[1],
      row[5],
      row[9],
      row[6],
      row[7],
      row[8],
      row[10],
      row[11],
      row[8],
      row[12],
      row[13],
    ];
    nav("/viewsales", { state: selectedData });
  };
  const [editableRows, setEditableRows] = useState({});
  return (
    <>
      <Nav />
      <div className="sales-container">
        <NavLink className="nav-link" to="/allsales">
          <button className="btn btn-success mb-3">Completed Customer</button>
        </NavLink>
        <h3>Current Customer</h3>
        <input
          type="search"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="form-control mb-3"
          placeholder="Search"
        />
        <div className="table-responsive">
          <table className="table table-bordered">
            <thead>
              <tr>
                <th>Edit</th>
                <th>Cid</th>
                <th>Salesid</th>
                <th>Cname</th>
                <th>Area</th>
                <th>Mobile No</th>
                <th>Product Name</th>
                <th>Sales Date</th>
                <th>Day</th>
                <th>Pay Date</th>
                <th>Price</th>
                <th>Balance</th>
                <th>Payment date</th>
                <th>Shift</th>
                <th>Timing</th>
                <th>Delete</th>
              </tr>
            </thead>
            <tbody>
              {filteredSales.map((d) => {
                const isEditable = editableRows[d[0]] || d[14] !== "s"; // local state OR initial backend status

                return (
                  <tr key={d[0]}>
                    <td>
                      {window.localStorage.getItem("pro") === "salman" && (
                        <input
                          type="checkbox"
                          checked={isEditable}
                          onChange={() => handleStatusToggle(d)}
                        />
                      )}
                    </td>
                    <td>
                      <button
                        className="btn btn-primary btn-sm"
                        onClick={() => viewSales(d)}
                      >
                        {d[0]}
                      </button>
                    </td>
                    <td>
                      <button
                        className="btn btn-danger btn-sm"
                        onClick={() => viewCustomer(d[1])}
                      >
                        {d[1]}
                      </button>
                    </td>

                    {/* Editable fields */}
                    {isEditable ? (
                      <>
                        <td>
                          <input
                            type="text"
                            value={d[2]}
                            onChange={(e) =>
                              updateField(d[0], 2, e.target.value)
                            }
                          />
                        </td>
                        <td>
                          <input
                            type="text"
                            value={d[3]}
                            onChange={(e) =>
                              updateField(d[0], 3, e.target.value)
                            }
                          />
                        </td>
                        <td>
                          <input
                            type="text"
                            value={d[4]}
                            onChange={(e) =>
                              updateField(d[0], 4, e.target.value)
                            }
                          />
                        </td>
                        <td>
                          <input
                            type="text"
                            value={d[5]}
                            onChange={(e) =>
                              updateField(d[0], 5, e.target.value)
                            }
                          />
                        </td>
                        <td>
                          <input
                            type="date"
                            value={d[6]}
                            onChange={(e) =>
                              updateField(d[0], 6, e.target.value)
                            }
                          />
                        </td>
                        <td>
                          <input
                            type="text"
                            value={d[7]}
                            onChange={(e) =>
                              updateField(d[0], 7, e.target.value)
                            }
                          />
                        </td>
                        <td>
                          <input
                            type="text"
                            value={d[8]}
                            onChange={(e) =>
                              updateField(d[0], 8, e.target.value)
                            }
                          />
                        </td>
                        <td>
                          <input
                            type="text"
                            value={d[9]}
                            onChange={(e) =>
                              updateField(d[0], 9, e.target.value)
                            }
                          />
                        </td>
                        <td>
                          <input
                            type="text"
                            value={d[10]}
                            onChange={(e) =>
                              updateField(d[0], 10, e.target.value)
                            }
                          />
                        </td>
                        <td>
                          <input
                            type="date"
                            value={d[11]}
                            onChange={(e) =>
                              updateField(d[0], 11, e.target.value)
                            }
                          />
                        </td>
                        `{" "}
                        <td>
                          <input
                            type="text"
                            value={d[15]}
                            onChange={(e) =>
                              updateField(d[0], 15, e.target.value)
                            }
                          />
                        </td>
                        `
                        <td>
                          <input
                            type="text"
                            value={d[16]}
                            onChange={(e) =>
                              updateField(d[0], 16, e.target.value)
                            }
                          />
                        </td>
                      </>
                    ) : (
                      <>
                        <td>{d[2]}</td>
                        <td>{d[3]}</td>
                        <td>{d[4]}</td>
                        <td>{d[5]}</td>
                        <td>{d[6]}</td>
                        <td>{d[7]}</td>
                        <td>{d[8]}</td>
                        <td>{d[9]}</td>
                        <td>{d[10]}</td>
                        <td>{d[11]}</td>
                        <td>{d[15]}</td>
                        <td>{d[16]}</td>
                      </>
                    )}

                    <td>
                      {window.localStorage.getItem("pro") === "salman" && (
                        <button
                          className="btn btn-danger btn-sm"
                          onClick={() => handleDelete(d[0])}
                          disabled={deleteMutation.isLoading}
                        >
                          {deleteMutation.isLoading ? "Deleting..." : "Delete"}
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
};

export default Sales;
